/**
 * ApiResult — the ONE named metadata channel (RES-CLIENT-001 §8.1).
 *
 * ## 🔴 The defect class this exists to end
 *
 * pos-web's response interceptor attaches metadata as `_meta`, a **non-enumerable
 * property on the returned array** (`responseProcessors.js:110-114`). Measured
 * survival:
 *
 *     _meta.pagination           {"page":2,"limit":20,"total":42000,"totalPages":2100}
 *     after [...rows]            undefined
 *     after rows.filter(Boolean) undefined
 *     after JSON round-trip      undefined
 *
 * A consumer that sorts, filters, memoizes or persists the list loses the total
 * silently and pages "1 of 1" over 42,000 rows — the scar `useCustomerData.js:121-123`
 * records in a comment.
 *
 * Its sibling `_advisory` is worse: it is **never set at all**. The server puts
 * `advisory` INSIDE `data` (`locations.write.js:114,190`); the interceptor looks for
 * it at the ENVELOPE level (`responseProcessors.js:44`). The multi-jurisdiction
 * advisory banner has therefore never rendered in production, and both of its tests
 * are green because they hand-build the post-interceptor shape — two green halves,
 * never compared.
 *
 * ⇒ A hidden, non-enumerable metadata channel is precisely the design that makes
 * this defect class invisible. Metadata reaches a caller through a **named,
 * always-present, enumerable, frozen** member or it does not reach them reliably.
 *
 * ## Why `pagination` and `meta` are OPAQUE
 *
 * §5.3(a): the client "must NOT promise a typed pagination object, because the
 * server does not deliver one". Converging the wire onto the canonical
 * `{limit, offset, total, nextCursor, hasMore}` is the SERVER's job (RES-CLIENT-001
 * Phase 0 / lane N9). A client that reshaped, validated or defaulted these members
 * would be a provenance sniffer — the disease §2 indicts at 13 sites across eleven
 * shared packages, not the cure.
 *
 * @module @puffin-core/api-client/facade/apiResult
 */

import { installDataTripwire } from './dataTripwire.js';

/**
 * Read by the `.data` tripwire. Names the correct member and the standard, so the
 * failure is self-explaining at the call site that caused it.
 */
const DATA_TRIPWIRE_MESSAGE =
  'ApiResult has no `data` — the payload is `.payload` (RES-CLIENT-001).';

/**
 * Build the frozen `ApiResult` handed back by every `api.withMeta.<verb>` call.
 *
 * Every key is ALWAYS present — `pagination` / `meta` / `requestId` are `null` when
 * the envelope carried none, never missing. Same reasoning `sendListResponse` used
 * for `nextCursor` (`response-helpers/success.js:109-119`): a consumer branching on
 * PRESENCE is a consumer that will one day branch wrong.
 *
 * `headers` is passed through **verbatim** — not copied, not frozen. Header
 * containers arrive in three shapes (axios `AxiosHeaders`, a plain bag, a fetch
 * `Headers`); copying to a plain object would lose case-insensitive `.get()`, and
 * freezing would mutate an object the transport owns. `readHeader` is the reader
 * that spans all three.
 *
 * The `fields` argument is REQUIRED — there is no `= {}` default. A result built
 * from nothing describes no response, so a bare call is a programming error and a
 * `TypeError` at the call site is the honest answer. (Wire it or prune it: a default
 * nothing calls is a branch nobody can test truthfully.)
 *
 * @param {Object} fields
 * @param {*} [fields.payload] - The envelope's `data`, verbatim — including null/0/''/false.
 * @param {*} [fields.pagination] - The envelope's `pagination`, opaque, or null.
 * @param {*} [fields.meta] - The envelope's `meta`, opaque, or null.
 * @param {number} [fields.status] - HTTP status.
 * @param {*} [fields.headers] - The response header container, verbatim.
 * @param {string|null} [fields.requestId] - `X-Request-Id`, or null.
 * @returns {Readonly<Object>} A frozen ApiResult.
 */
export function createApiResult({ payload, pagination, meta, status, headers, requestId }) {
  const result = {
    payload,
    pagination: pagination ?? null,
    meta: meta ?? null,
    status,
    headers: headers ?? null,
    requestId: requestId ?? null,
  };

  installDataTripwire(result, DATA_TRIPWIRE_MESSAGE);

  return Object.freeze(result);
}
